// FilterSelect — Unreal-content-browser-style picker: a closed field that
// opens a panel with a search box at the top and the filtered option list
// below it ("Start typing to search"). Replaces the native <select> in the
// asset wizards, where clip lists run to 60+ entries.
//
// Filtering is the pure filterOptions (src/lib/optionFilter.js, unit-tested).

import React, { useEffect, useRef, useState } from 'react'
import { inputStyle, tokens } from './WizardShell'
import { filterOptions } from '../../../lib/optionFilter'

export default function FilterSelect({ value, onChange, options, placeholder = '— none —' }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const wrapRef = useRef(null)
  const searchRef = useRef(null)

  const selected = value ? options.find(o => o.rawId === value) : null
  const filtered = filterOptions(options, query)

  // Close on click outside.
  useEffect(() => {
    if (!open) return
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // Fresh search each open, focused and ready to type.
  useEffect(() => {
    if (open) {
      setQuery('')
      // after the panel mounts
      requestAnimationFrame(() => searchRef.current?.focus())
    }
  }, [open])

  const pick = (id) => { onChange(id); setOpen(false) }

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); return }
    if (e.key === 'Enter') {
      e.preventDefault()
      if (filtered.length > 0) pick(filtered[0].rawId)
    }
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          ...inputStyle,
          height: 28,
          padding: '2px 8px',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: selected ? tokens.text : tokens.dim,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected?.name ?? placeholder}
        </span>
        <span style={{ color: tokens.dim, marginLeft: 8 }}>▾</span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 40,
            marginTop: 2,
            background: tokens.bg,
            border: `1px solid ${tokens.border}`,
            borderRadius: 4,
            boxShadow: 'var(--m-shadow-card)',
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: 6, borderBottom: `1px solid ${tokens.border}` }}>
            <input
              ref={searchRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Start typing to search"
              style={{ ...inputStyle, height: 24, padding: '2px 8px', margin: 0 }}
            />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            <Row label={placeholder} dim active={!value} onClick={() => pick(null)} />
            {filtered.map(o => (
              <Row key={o.rawId} label={o.name} active={o.rawId === value} onClick={() => pick(o.rawId)} />
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: '8px 10px', fontFamily: 'var(--m-font)', fontSize: 10, color: tokens.dim }}>
                no clips match "{query}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function Row({ label, onClick, active, dim }) {
  const [hover, setHover] = useState(false)
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '5px 10px',
        fontFamily: 'var(--m-font)',
        fontSize: 11,
        cursor: 'pointer',
        color: dim ? tokens.dim : tokens.text,
        background: active
          ? 'color-mix(in srgb, var(--m-accent) 18%, transparent)'
          : hover ? 'color-mix(in srgb, var(--m-fg) 8%, transparent)' : 'transparent',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {label}
    </div>
  )
}
